<template lang="html">
  <div class="connector-layout">
    <page-header :header-class="'header--start-charging'">
      <h1 class="start-charging__title">
        {{ $t("chargingPage.connectorLayout.title") }}
      </h1>
      <div class="start-charging__info">
        <img
          class="start-charging__icon"
          src="@/assets/images/info-circle.svg"
          alt=""
        />
        <p>
          {{ $t("chargingPage.connectorLayout.subTitle") }}
        </p>
      </div>
    </page-header>
    <main class="start-charging__main">
      <form class="form-group" @submit.prevent="search()">
        <label class="label">
          {{ $t("chargingPage.connectorLayout.plsTypeChargerId") }}
        </label>
        <div class="input-with-icon">
          <input
            v-model="chargerId"
            class="input"
            type="number"
            :placeholder="$t('chargingPage.connectorLayout.chargerId')"
          />
          <picture class="input-icon" @click="search()">
            <source
              srcset="@/assets/images/input-search-dark.svg"
              media="(prefers-color-scheme: dark)"
            />
            <img src="@/assets/images/input-search.svg" alt="" />
          </picture>
        </div>
        <div class="input-info">
          <p>
            {{ address }}
          </p>
        </div>
      </form>

      <form v-if="connectors.length > 0" class="form-group">
        <label class="label">
          {{ $t("chargingPage.connectorLayout.hint") }}
        </label>
        <loading-component v-if="startCallIsLoading" />
        <connectors-component
          v-else
          :connectors="connectors"
          @connectorselected="connectorSelected"
        />
      </form>

      <loading-component v-if="submitIsLoading" />
    </main>
    <confirm-modal
      v-if="isConfirmModalActive"
      yes-button-color-class="btn-primary"
      :main-title="confirmMainTitle"
      :sub-title="$t('chargingPage.confirm.startCharging.subTitle')"
      :yes-title="$t('chargingPage.confirm.startCharging.yesTitle')"
      :no-title="$t('chargingPage.confirm.startCharging.noTitle')"
      @confirm="confirmCharge"
      @cancel="handleConfirmModalCancel"
    />
    <feedback-modal
      v-if="isFeedbackModalActive"
      :main-title="$t('errors.connectorErrors.notFoundError.mainTitle')"
      :sub-title="$t('errors.connectorErrors.notFoundError.subTitle')"
      :success="false"
      @confirm="handleFeedbackModalOkClick"
    />
  </div>
</template>

<script lang="ts" setup>
import { GuestChargingService } from "@/services/guest-charging-service"
import ConfirmModal from "@/components/ui/ConfirmModal"
import FeedbackModal from "@/components/ui/FeedbackModal"
import ConnectorsComponent from "@/components/ui/ConnectorsComponent"
import PageHeader from "../ui/PageHeader.vue"
import LoadingComponent from "../ui/LoadingComponent.vue"
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Connector } from "@/models/common"

interface ConnectorLayoutProps {
  startCallIsLoading: boolean
}

const { startCallIsLoading = false } = defineProps<ConnectorLayoutProps>()

const emit = defineEmits(["connectorchanged"])

const { t } = useI18n()

const chargerId = ref("")
const connectors = ref([])
const selectedConnector = ref<Connector>({})
const confirmMainTitle = ref("")
const address = ref("")
const isConfirmModalActive = ref(false)
const isFeedbackModalActive = ref(false)
const submitIsLoading = ref(false)

const confirmCharge = () => {
  emit("connectorchanged", {
    connector: selectedConnector.value,
    address: address.value
  })
  isConfirmModalActive.value = false
}

const connectorSelected = (connector: Connector) => {
  selectedConnector.value = connector
  confirmMainTitle.value = t("chargingPage.confirm.startCharging.mainTitle", {
    label: selectedConnector.value.label
  })
  isConfirmModalActive.value = true
}

const feedbackPopUp = () => {
  isFeedbackModalActive.value = true
}

const search = async () => {
  submitIsLoading.value = true
  connectors.value = []
  address.value = ""

  try {
    const resp = await GuestChargingService.getChargingStations(chargerId.value)
    if (resp?.data) {
      address.value = resp.data.address
      connectors.value = resp.data.connectors
    }
  } catch {
    feedbackPopUp()
  } finally {
    submitIsLoading.value = false
  }
}

const handleFeedbackModalOkClick = () => {
  isFeedbackModalActive.value = false
}

const handleConfirmModalCancel = () => {
  isConfirmModalActive.value = false
}
</script>
